import React, { useState, useRef, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { FaTimes, FaPause, FaPlay, FaCheck } from 'react-icons/fa';
import './VoiceRecorder.css';

const VoiceRecorder = ({ onRecordingComplete, onCancel, maxDuration = 30 }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);

  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const recordPluginRef = useRef(null);
  const timerRef = useRef(null);
  const isStoppingRef = useRef(false);

  useEffect(() => {
    if (!waveformRef.current) return;

    // Local cancelled flag — NOT a ref, so each effect instance has its own copy
    let cancelled = false;

    waveformRef.current.innerHTML = '';

    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#c4b5a5',
      progressColor: '#8B7355',
      cursorColor: '#dc3545',
      cursorWidth: 2,
      barWidth: 3,
      barGap: 2,
      barRadius: 3,
      height: 64,
      normalize: true,
    });

    const record = wavesurfer.registerPlugin(RecordPlugin.create({
      scrollingWaveform: false,
      continuousWaveform: true,
      continuousWaveformDuration: maxDuration,
      renderRecordedAudio: false,
      audioBitsPerSecond: 128000,
      mimeType: 'audio/webm;codecs=opus',
    }));

    wavesurferRef.current = wavesurfer;
    recordPluginRef.current = record;

    record.on('record-end', (blob) => {
      // Ignore record-end from a cancelled/cleaned-up instance (React strict mode)
      if (cancelled) return;

      setIsRecording(false);
      isStoppingRef.current = false;

      // Release mic: stop stream tracks, stopMic, destroy wavesurfer
      cleanup();

      if (blob && blob.size > 0 && onRecordingComplete) {
        onRecordingComplete(blob);
      }
    });

    // Start recording
    const startAsync = async () => {
      try {
        await record.startRecording();
        if (cancelled) {
          // Effect was cleaned up while awaiting — kill everything
          cleanup();
          return;
        }
        setIsRecording(true);

        let elapsed = 0;
        timerRef.current = setInterval(() => {
          elapsed += 1;
          setDuration(elapsed);
          if (elapsed >= maxDuration) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            stopRecording();
          }
        }, 1000);
      } catch (err) {
        // startRecording failed or was interrupted — clean up any stream that was created
        cleanup();
        if (!cancelled) {
          console.error('Failed to start recording:', err);
          setError('Failed to access microphone. Please grant permission and try again.');
        }
      }
    };

    const cleanup = () => {
      try {
        if (record.stream) {
          record.stream.getTracks().forEach(t => t.stop());
          record.stream = null;
        }
      } catch (e) { /* ignore */ }
      try { record.stopMic(); } catch (e) { /* ignore */ }
      try { wavesurfer.destroy(); } catch (e) { /* ignore */ }
      // Only null out refs if they still point to THIS instance
      if (wavesurferRef.current === wavesurfer) wavesurferRef.current = null;
      if (recordPluginRef.current === record) recordPluginRef.current = null;
    };

    startAsync();

    return () => {
      cancelled = true;

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      cleanup();
    };
  }, []);

  const pauseRecording = () => {
    if (recordPluginRef.current && recordPluginRef.current.pauseRecording) {
      recordPluginRef.current.pauseRecording();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const resumeRecording = () => {
    if (recordPluginRef.current && recordPluginRef.current.resumeRecording) {
      recordPluginRef.current.resumeRecording();
      setIsPaused(false);

      let elapsed = duration;
      timerRef.current = setInterval(() => {
        elapsed += 1;
        setDuration(elapsed);
        if (elapsed >= maxDuration) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          stopRecording();
        }
      }, 1000);
    }
  };

  const stopRecording = async () => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      if (!recordPluginRef.current) {
        isStoppingRef.current = false;
        return;
      }
      if (recordPluginRef.current.isRecording()) {
        await recordPluginRef.current.stopRecording();
      } else {
        setIsRecording(false);
        isStoppingRef.current = false;
      }
    } catch (err) {
      setError('Failed to stop recording: ' + err.message);
      isStoppingRef.current = false;
    }
  };

  const handleCancel = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const record = recordPluginRef.current;
    const ws = wavesurferRef.current;

    if (record) {
      try { if (record.isRecording()) record.stopRecording(); } catch (e) { /* ignore */ }
      try {
        if (record.stream) {
          record.stream.getTracks().forEach(t => t.stop());
          record.stream = null;
        }
      } catch (e) { /* ignore */ }
      try { record.stopMic(); } catch (e) { /* ignore */ }
    }
    if (ws) {
      try { ws.destroy(); } catch (e) { /* ignore */ }
    }
    wavesurferRef.current = null;
    recordPluginRef.current = null;

    setIsRecording(false);
    isStoppingRef.current = false;
    if (onCancel) onCancel();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const remainingTime = Math.max(0, maxDuration - duration);

  return (
    <div className="voice-recorder">
      <div className="voice-recorder-header">
        <div className="recording-indicator">
          <span className="recording-dot"></span>
          <span className="recording-text">Recording</span>
        </div>
        <div className="recording-timer">
          {formatTime(duration)} / {formatTime(maxDuration)}
        </div>
      </div>

      {error && (
        <div className="voice-recorder-error">
          {error}
        </div>
      )}

      <div className="waveform-container" ref={waveformRef}></div>

      <div className="voice-recorder-controls">
        <button
          type="button"
          className="recorder-btn recorder-btn-cancel"
          onClick={handleCancel}
        >
          <FaTimes /> Cancel
        </button>

        {isRecording && !isPaused && (
          <button
            type="button"
            className="recorder-btn recorder-btn-pause"
            onClick={pauseRecording}
          >
            <FaPause /> Pause
          </button>
        )}

        {isPaused && (
          <button
            type="button"
            className="recorder-btn recorder-btn-resume"
            onClick={resumeRecording}
          >
            <FaPlay /> Resume
          </button>
        )}

        <button
          type="button"
          className="recorder-btn recorder-btn-stop"
          onClick={stopRecording}
          disabled={duration < 1 || isStoppingRef.current}
        >
          <FaCheck /> Done
        </button>
      </div>

      {remainingTime <= 5 && remainingTime > 0 && (
        <div className="time-warning">
          {remainingTime} second{remainingTime !== 1 ? 's' : ''} remaining
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
